/**
 * Shared front logic for Site poll and See results blocks.
 */

import apiFetch from "@wordpress/api-fetch";

const PEEK_STORAGE_PREFIX = "kraken_poll_peek_";

/**
 * @param {string} fullUrl
 * @returns {string} REST path starting with /kraken-core/...
 */
export function restPathFromFullUrl(fullUrl) {
	try {
		const u = new URL(fullUrl, window.location.origin);
		const wpJson = "/wp-json";
		const i = u.pathname.indexOf(wpJson);
		if (i === -1) {
			return u.pathname;
		}
		return u.pathname.slice(i + wpJson.length) || "/";
	} catch {
		return "";
	}
}

/** @type {Map<string, Promise<object>>} */
const pollFetchCache = new Map();

/**
 * @param {HTMLElement} root Site poll root (.kraken-site-poll)
 * @returns {Promise<object>}
 */
export function fetchPollState(root) {
	const restUrl = root.getAttribute("data-rest-url") || "";
	const nonce = root.getAttribute("data-rest-nonce") || "";
	const path = restPathFromFullUrl(restUrl);
	if (!path) {
		return Promise.resolve({});
	}
	const key = path;
	if (!pollFetchCache.has(key)) {
		pollFetchCache.set(
			key,
			apiFetch({
				path,
				headers: nonce ? { "X-WP-Nonce": nonce } : {},
			}).catch(() => ({})),
		);
	}
	return pollFetchCache.get(key);
}

/**
 * Fetch poll JSON using a full REST index URL + nonce (e.g. from See results block).
 *
 * @param {string} restUrl
 * @param {string} nonce
 * @returns {Promise<object>}
 */
export function fetchPollStateByUrl(restUrl, nonce) {
	const path = restPathFromFullUrl(restUrl);
	if (!path) {
		return Promise.resolve({});
	}
	const key = path;
	if (!pollFetchCache.has(key)) {
		pollFetchCache.set(
			key,
			apiFetch({
				path,
				headers: nonce ? { "X-WP-Nonce": nonce } : {},
			}).catch(() => ({})),
		);
	}
	return pollFetchCache.get(key);
}

/**
 * @param {string} pollId
 * @returns {boolean}
 */
export function hasPeekResultsEnabled(pollId) {
	try {
		return sessionStorage.getItem(`${PEEK_STORAGE_PREFIX}${pollId}`) === "1";
	} catch {
		return false;
	}
}

/**
 * @param {string} pollId
 */
export function setPeekResultsEnabled(pollId) {
	try {
		sessionStorage.setItem(`${PEEK_STORAGE_PREFIX}${pollId}`, "1");
	} catch {
		// ignore
	}
}

/**
 * @param {string} pollId
 */
export function clearPeekResults(pollId) {
	try {
		sessionStorage.removeItem(`${PEEK_STORAGE_PREFIX}${pollId}`);
	} catch {
		// ignore
	}
}

/**
 * @param {HTMLElement} root
 * @param {object} data
 */
export function applyCounts(root, data) {
	const ow = root.querySelector('[data-kraken-poll-count="old_west"]');
	const nf = root.querySelector('[data-kraken-poll-count="new_frontier"]');
	if (ow && typeof data.oldWest === "number") {
		ow.textContent = String(data.oldWest);
	}
	if (nf && typeof data.newFrontier === "number") {
		nf.textContent = String(data.newFrontier);
	}
	const countsEl = root.querySelector("[data-kraken-poll-counts]");
	if (countsEl) {
		countsEl.removeAttribute("hidden");
		countsEl.classList.remove("kraken-site-poll__counts--pending");
	}
}

/**
 * @param {HTMLElement} root
 * @param {boolean} disabled
 */
export function setPollLinksDisabled(root, disabled) {
	root.querySelectorAll('[data-kraken-poll-interactive="1"]').forEach((el) => {
		if (disabled) {
			el.setAttribute("aria-disabled", "true");
			el.setAttribute("tabindex", "-1");
		} else {
			el.removeAttribute("aria-disabled");
			el.removeAttribute("tabindex");
		}
	});
}

/**
 * @param {HTMLElement} root
 * @param {object} data
 */
export function applyStateToRoot(root, data) {
	const pollId = root.getAttribute("data-kraken-poll-id");
	const peek = pollId && hasPeekResultsEnabled(pollId);

	if (data.hasVoted) {
		root.classList.add("is-voted");
		root.classList.remove("kraken-site-poll--peek-results");
		if (pollId) {
			clearPeekResults(pollId);
		}
		applyCounts(root, data);
		setPollLinksDisabled(root, true);
	} else if (peek) {
		root.classList.add("kraken-site-poll--peek-results");
		root.classList.remove("is-voted");
		applyCounts(root, data);
		setPollLinksDisabled(root, false);
		const countsEl = root.querySelector("[data-kraken-poll-counts]");
		if (countsEl) {
			countsEl.removeAttribute("hidden");
		}
	} else {
		root.classList.remove("is-voted", "kraken-site-poll--peek-results");
		setPollLinksDisabled(root, false);
		const countsEl = root.querySelector("[data-kraken-poll-counts]");
		if (countsEl) {
			countsEl.setAttribute("hidden", "");
		}
	}
}

/**
 * Show or hide all "See results" blocks for a poll (e.g. after vote or initial fetch).
 *
 * @param {string} pollId
 * @param {boolean} hasVoted
 */
export function syncSeeResultsVisibilityForPoll(pollId, hasVoted) {
	if (!pollId) {
		return;
	}
	document
		.querySelectorAll(
			`.kraken-site-poll-see-results[data-kraken-poll-id="${CSS.escape(pollId)}"]`,
		)
		.forEach((el) => {
			if (hasVoted) {
				el.setAttribute("hidden", "");
			} else {
				el.removeAttribute("hidden");
			}
		});
}

export function applyStateToAllSamePoll(root, data) {
	const id = root.getAttribute("data-kraken-poll-id");
	if (!id) {
		return;
	}
	document
		.querySelectorAll(`.kraken-site-poll[data-kraken-poll-id="${CSS.escape(id)}"]`)
		.forEach((el) => {
			applyStateToRoot(el, data);
		});
	if (typeof data.hasVoted === "boolean") {
		syncSeeResultsVisibilityForPoll(id, data.hasVoted);
	}
}

let seeResultsVisibilityBootstrapped = false;

/**
 * Fetch poll state for each See results block (e.g. page has no Site poll block).
 */
function bootstrapSeeResultsVisibility() {
	if (seeResultsVisibilityBootstrapped) {
		return;
	}
	seeResultsVisibilityBootstrapped = true;
	document
		.querySelectorAll(".kraken-site-poll-see-results[data-kraken-poll-id]")
		.forEach((wrap) => {
			const pollId = wrap.getAttribute("data-kraken-poll-id");
			const restUrl = wrap.getAttribute("data-rest-url") || "";
			const nonce = wrap.getAttribute("data-rest-nonce") || "";
			if (!pollId || !restUrl) {
				return;
			}
			fetchPollStateByUrl(restUrl, nonce).then((data) => {
				if (data && typeof data.hasVoted === "boolean") {
					syncSeeResultsVisibilityForPoll(pollId, data.hasVoted);
				}
			});
		});
}

/**
 * @param {HTMLElement} root
 * @param {string} choice
 */
async function postVote(root, choice) {
	const restUrl = root.getAttribute("data-rest-url") || "";
	const nonce = root.getAttribute("data-rest-nonce") || "";
	const basePath = restPathFromFullUrl(restUrl);
	const path = `${basePath.replace(/\/$/, "")}/vote`;
	try {
		const data = await apiFetch({
			path,
			method: "POST",
			data: { choice },
			headers: nonce ? { "X-WP-Nonce": nonce } : {},
		});
		pollFetchCache.delete(basePath);
		applyStateToAllSamePoll(root, data);
	} catch (err) {
		const code = err && err.code;
		if (code === "already_voted" || (err && err.data && err.data.status === 409)) {
			pollFetchCache.delete(basePath);
			const d = await fetchPollState(root);
			applyStateToAllSamePoll(root, d);
		}
	}
}

/**
 * @param {HTMLElement} root
 */
function bindRoot(root) {
	root.addEventListener(
		"click",
		(e) => {
			const t = e.target;
			if (!(t instanceof Element)) {
				return;
			}
			const target = t.closest("[data-kraken-poll-choice][data-kraken-poll-interactive]");
			if (target && root.contains(target) && root.classList.contains("is-voted")) {
				e.preventDefault();
				e.stopPropagation();
				return;
			}
			if (root.classList.contains("is-voted")) {
				return;
			}
			if (!target || !root.contains(target)) {
				return;
			}
			e.preventDefault();
			e.stopPropagation();
			const choice = target.getAttribute("data-kraken-poll-choice");
			if (choice !== "old_west" && choice !== "new_frontier") {
				return;
			}
			postVote(root, choice);
		},
		true,
	);
}

let seeResultsDelegated = false;

/**
 * One delegated listener: reveal counts on all Site poll blocks for this poll id (no vote).
 */
export function initSeeResultsBlocks() {
	if (!seeResultsDelegated) {
		seeResultsDelegated = true;
		document.addEventListener(
			"click",
			(e) => {
				const t = e.target;
				if (!(t instanceof Element)) {
					return;
				}
				const trigger = t.closest("[data-kraken-poll-see-results-trigger]");
				if (!trigger) {
					return;
				}
				e.preventDefault();
				const wrap = trigger.closest(".kraken-site-poll-see-results");
				if (!wrap) {
					return;
				}
				const pollId = wrap.getAttribute("data-kraken-poll-id");
				const restUrl = wrap.getAttribute("data-rest-url") || "";
				const nonce = wrap.getAttribute("data-rest-nonce") || "";
				if (!pollId || !restUrl) {
					return;
				}
				setPeekResultsEnabled(pollId);
				const basePath = restPathFromFullUrl(restUrl);
				pollFetchCache.delete(basePath);
				fetchPollStateByUrl(restUrl, nonce).then((data) => {
					if (!data || typeof data.oldWest !== "number") {
						return;
					}
					document
						.querySelectorAll(
							`.kraken-site-poll[data-kraken-poll-id="${CSS.escape(pollId)}"]`,
						)
						.forEach((root) => {
							applyStateToRoot(root, data);
						});
					if (typeof data.hasVoted === "boolean") {
						syncSeeResultsVisibilityForPoll(pollId, data.hasVoted);
					}
				});
			},
			true,
		);
	}
	bootstrapSeeResultsVisibility();
}

/**
 * Boot Site poll voting roots.
 */
export function initSitePollBlocks() {
	const roots = document.querySelectorAll(".kraken-site-poll[data-kraken-poll-id]");
	roots.forEach((root) => {
		bindRoot(root);
		fetchPollState(root).then((data) => {
			if (data && typeof data.hasVoted === "boolean") {
				applyStateToAllSamePoll(root, data);
			}
		});
	});
	initSeeResultsBlocks();
}
